import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListBudgetOverviewQueryKey,
  useDeleteBudgetTarget,
  useListBudgetOverview,
  useUpsertBudgetTarget,
} from "@workspace/api-client-react";
import type { BudgetVariance } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronsUpDown,
  CircleDollarSign,
  Clock3,
  Pencil,
  Plus,
  RefreshCw,
  Target,
  Trash2,
  X,
} from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";

type Metric = "sales" | "expenses";
type Scope = "store" | "branch" | "category";
type BudgetForm = {
  metric: Metric;
  scopeType: Scope;
  scopeId: string;
  targetAmount: string;
  customCategory: string;
};

const monthNow = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const money = (amount: number) =>
  `₦${Number(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthLabel = (month: string) => {
  if (!month) return "";
  const [year, numericMonth] = month.split("-").map(Number);
  return new Date(year, numericMonth - 1, 1).toLocaleDateString("en-NG", { month: "long", year: "numeric" });
};

function SearchableCategoryPicker({
  id,
  value,
  options,
  placeholder,
  testId,
  onChange,
  disabled = false,
}: {
  id: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  testId: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className="h-11 w-full justify-between rounded-lg border-[#d5e0d6] bg-white px-3 text-left text-sm font-normal text-[#365248] hover:bg-[#f7f9f6] disabled:bg-[#f5f7f4]"
        >
          <span className="truncate">{selectedLabel ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-[#829087]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[60] w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder="Search categories…" />
          <CommandList>
            <CommandEmpty>No matching category.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === option.value ? "opacity-100" : "opacity-0"}`} />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const blankForm = (): BudgetForm => ({
  metric: "sales",
  scopeType: "store",
  scopeId: "all",
  targetAmount: "",
  customCategory: "",
});

function BudgetContent() {
  const [month, setMonth] = useState(monthNow);
  const [filter, setFilter] = useState<"all" | Metric>("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BudgetVariance | null>(null);
  const [form, setForm] = useState<BudgetForm>(blankForm);
  const [formError, setFormError] = useState("");
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; message: string } | null>(null);
  const queryClient = useQueryClient();
  const query = useListBudgetOverview({ month });
  const overview = query.data;
  const saveTarget = useUpsertBudgetTarget();
  const deleteTarget = useDeleteBudgetTarget();

  const targets = useMemo(
    () => (overview?.targets ?? []).filter((target) => filter === "all" || target.metric === filter),
    [overview?.targets, filter],
  );

  const showForm = (target?: BudgetVariance) => {
    setFeedback(null);
    setFormError("");
    if (target) {
      setEditing(target);
      setForm({
        metric: target.metric,
        scopeType: target.scopeType,
        scopeId: target.scopeId,
        targetAmount: String(target.targetAmount),
        customCategory: target.metric === "expenses" && target.scopeType === "category" ? target.scopeName : "",
      });
    } else {
      setEditing(null);
      setForm(blankForm());
    }
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setFormError("");
    setForm(blankForm());
  };

  const updateMonth = (value: string) => {
    if (!value) return;
    setMonth(value);
    setFeedback(null);
    void queryClient.invalidateQueries({ queryKey: getListBudgetOverviewQueryKey({ month: value }) });
  };

  const updateMetric = (metric: Metric) => {
    setForm((current) => ({
      ...current,
      metric,
      scopeType: "store",
      scopeId: "all",
      customCategory: "",
    }));
  };

  const updateScope = (scopeType: Scope) => {
    setForm((current) => ({
      ...current,
      scopeType,
      scopeId: scopeType === "store" ? "all" : "",
      customCategory: "",
    }));
  };

  const save = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const targetAmount = Number(form.targetAmount);
    const categoryName = form.customCategory.trim();
    const scopeId = form.metric === "expenses" && form.scopeType === "category" && categoryName
      ? categoryName
      : form.scopeId;
    if (!Number.isFinite(targetAmount) || targetAmount <= 0) {
      setFormError("Enter a target greater than zero.");
      return;
    }
    if (!scopeId || scopeId === "custom") {
      setFormError("Choose a scope before saving.");
      return;
    }
    if (form.metric === "expenses" && form.scopeType === "category" && (!scopeId.trim() || scopeId.trim().length > 80)) {
      setFormError("Expense category must be between 1 and 80 characters.");
      return;
    }
    const normalizedScopeId = form.metric === "expenses" && form.scopeType === "category" ? scopeId.trim() : scopeId;
    saveTarget.mutate(
      {
        data: {
          month,
          metric: form.metric,
          scopeType: form.scopeType,
          scopeId: normalizedScopeId,
          targetAmount,
        },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getListBudgetOverviewQueryKey({ month }) });
          setFeedback({ kind: "success", message: editing ? "Budget target updated." : "Budget target saved." });
          closeForm();
        },
        onError: () => setFormError("We couldn’t save this target. Check your connection and try again."),
      },
    );
  };

  const remove = (target: BudgetVariance) => {
    const confirmed = window.confirm(`Delete the ${target.scopeName} ${target.metric} target for ${monthLabel(month)}?`);
    if (!confirmed) return;
    setFeedback(null);
    deleteTarget.mutate(
      { id: target.id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getListBudgetOverviewQueryKey({ month }) });
          setFeedback({ kind: "success", message: `${target.scopeName} target deleted.` });
        },
        onError: () => setFeedback({ kind: "error", message: "Couldn’t delete this target. Please try again." }),
      },
    );
  };

  const selectedScopeOptions = form.metric === "sales"
    ? overview?.salesCategories ?? []
    : [];
  const isCategory = form.scopeType === "category";
  const isCustomExpenseCategory = form.metric === "expenses" && isCategory && form.scopeId === "custom";
  const targetCount = overview?.targets.length ?? 0;
  const targetSalesTotal = (overview?.targets ?? [])
    .filter((item) => item.metric === "sales" && item.scopeType === "store")
    .reduce((sum, item) => sum + item.targetAmount, 0);
  const targetExpenseTotal = (overview?.targets ?? [])
    .filter((item) => item.metric === "expenses" && item.scopeType === "store")
    .reduce((sum, item) => sum + item.targetAmount, 0);

  if (query.isLoading) {
    return (
      <div className="space-y-6" data-testid="budget-loading">
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1].map((item) => <div key={item} className="h-36 animate-pulse rounded-xl border border-[#e3e8e4] bg-[#f0f4f1]" />)}
        </div>
        <div className="space-y-3 rounded-xl border border-[#e3e8e4] bg-white p-5">
          <div className="h-6 w-48 animate-pulse rounded bg-[#edf1ed]" />
          {[0, 1, 2, 3].map((item) => <div key={item} className="h-12 animate-pulse rounded bg-[#f3f6f3]" />)}
        </div>
        <p className="sr-only">Loading monthly budget data</p>
      </div>
    );
  }

  if (query.isError || !overview) {
    return (
      <div className="rounded-xl border border-[#ead9d3] bg-[#fff9f6] p-8 text-center" data-testid="budget-error-state">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#f7e6df] text-[#a94f38]">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-semibold text-[#243b36]">Budget data didn’t load</h2>
        <p className="mt-1 text-sm text-[#687972]">Your saved targets are safe. Try loading this month again.</p>
        <button onClick={() => void query.refetch()} data-testid="button-retry-budget-load" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#174f45] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#103d35] active:scale-[.98]">
          <RefreshCw className="h-4 w-4" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-[#243b36]" data-testid="budget-content">
      <section className="relative overflow-hidden rounded-2xl border border-[#dce6de] bg-[#f3f7f2] px-5 py-6 sm:px-7">
        <div className="pointer-events-none absolute -right-12 -top-20 h-64 w-64 rounded-full border-[32px] border-[#dce9dc]/65" />
        <div className="pointer-events-none absolute right-20 top-24 h-28 w-28 rounded-full border border-[#d1dfd3]" />
        <div className="relative flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div>
            <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[.17em] text-[#6c8374]">
              <span className="h-px w-6 bg-[#96ae99]" /> Planning / Monthly
            </div>
            <h2 className="font-['Manrope'] text-3xl font-extrabold tracking-[-.04em] text-[#203b34] sm:text-[2.5rem]">Budgets</h2>
            <p className="mt-1.5 max-w-lg text-sm leading-6 text-[#657970]">Set the month’s numbers, then keep every target honest against live POS activity.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="budget-month" className="text-xs font-semibold uppercase tracking-[.12em] text-[#6c8374]">Month</label>
            <input
              id="budget-month"
              type="month"
              value={month}
              onChange={(event) => updateMonth(event.target.value)}
              data-testid="input-budget-month"
              className="h-11 rounded-lg border border-[#cfdbd1] bg-[#fffefa] px-3 text-sm font-semibold text-[#29453d] shadow-sm outline-none transition focus:border-[#548276] focus:ring-2 focus:ring-[#b7d0bf]/60"
            />
            <button onClick={() => showForm()} data-testid="button-add-budget-target" className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#174f45] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#103d35] active:scale-[.98]">
              <Plus className="h-4 w-4" /> Add target
            </button>
          </div>
        </div>
      </section>

      {feedback && (
        <div role="status" data-testid={`status-budget-${feedback.kind}`} className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${feedback.kind === "success" ? "border-[#d0e3d1] bg-[#f1f8f0] text-[#315f3a]" : "border-[#ead9d3] bg-[#fff8f4] text-[#9b4934]"}`}>
          {feedback.kind === "success" ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <span>{feedback.message}</span>
          <button onClick={() => setFeedback(null)} data-testid="button-dismiss-budget-feedback" aria-label="Dismiss message" className="ml-auto rounded p-1 transition hover:bg-black/5"><X className="h-4 w-4" /></button>
        </div>
      )}

      <section className="grid gap-4 sm:grid-cols-2" aria-label="Monthly actuals">
        <article data-testid="card-budget-sales-summary" className="group rounded-xl border border-[#dce6de] bg-[#fffefa] p-5 shadow-[0_2px_10px_rgba(28,64,48,.035)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(28,64,48,.07)]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.14em] text-[#789080]">Sales · actual</p>
              <p className="mt-2 font-['IBM_Plex_Mono'] text-2xl font-medium tracking-tight tabular-nums text-[#214c40]" data-testid="value-budget-actual-sales">{money(overview.actuals.sales)}</p>
            </div>
            <div className="rounded-lg bg-[#e8f1e6] p-2.5 text-[#397253]"><ArrowUpRight className="h-5 w-5" /></div>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-[#e8eee8] pt-3">
            <span className="text-xs text-[#73847a]">Store target</span>
            <span className="font-['IBM_Plex_Mono'] text-xs font-medium tabular-nums text-[#466458]" data-testid="value-budget-target-sales">{targetSalesTotal ? money(targetSalesTotal) : "Not set"}</span>
          </div>
        </article>
        <article data-testid="card-budget-expenses-summary" className="group rounded-xl border border-[#dce6de] bg-[#fffefa] p-5 shadow-[0_2px_10px_rgba(28,64,48,.035)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(28,64,48,.07)]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.14em] text-[#9a7767]">Expenses · actual</p>
              <p className="mt-2 font-['IBM_Plex_Mono'] text-2xl font-medium tracking-tight tabular-nums text-[#62493e]" data-testid="value-budget-actual-expenses">{money(overview.actuals.expenses)}</p>
            </div>
            <div className="rounded-lg bg-[#f4eae3] p-2.5 text-[#a26e50]"><ArrowDownRight className="h-5 w-5" /></div>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-[#eee6e0] pt-3">
            <span className="text-xs text-[#89766d]">Store target</span>
            <span className="font-['IBM_Plex_Mono'] text-xs font-medium tabular-nums text-[#71584b]" data-testid="value-budget-target-expenses">{targetExpenseTotal ? money(targetExpenseTotal) : "Not set"}</span>
          </div>
        </article>
      </section>

      <section className="overflow-hidden rounded-xl border border-[#dce6de] bg-[#fffefa] shadow-[0_3px_14px_rgba(28,64,48,.035)]" data-testid="section-budget-targets">
        <div className="flex flex-col justify-between gap-4 border-b border-[#e8eee8] px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-[#4e7967]" />
              <h3 className="text-base font-bold tracking-tight text-[#28473d]">Target comparison</h3>
            </div>
            <p className="mt-1 text-xs text-[#829087]" data-testid="text-budget-period">{monthLabel(month)} · {targetCount} {targetCount === 1 ? "target" : "targets"}</p>
          </div>
          <div className="flex items-center gap-2 self-start rounded-lg bg-[#f1f5f0] p-1 sm:self-auto" role="tablist" aria-label="Filter budget targets">
            {(["all", "sales", "expenses"] as const).map((option) => (
              <button
                key={option}
                role="tab"
                aria-selected={filter === option}
                onClick={() => setFilter(option)}
                data-testid={`tab-budget-${option}`}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition ${filter === option ? "bg-white text-[#245446] shadow-sm" : "text-[#78877e] hover:text-[#36594b]"}`}
              >
                {option === "all" ? "All targets" : option}
              </button>
            ))}
          </div>
        </div>
        {targets.length === 0 ? (
          <div className="px-6 py-14 text-center" data-testid="budget-empty-state">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#dce8dc] bg-[#f2f7f0] text-[#5e846c]">
              {targetCount ? <ChevronDown className="h-5 w-5" /> : <CircleDollarSign className="h-5 w-5" />}
            </div>
            <h4 className="mt-4 text-sm font-bold text-[#355348]">{targetCount ? "No targets in this view" : "Give this month a target"}</h4>
            <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-[#7a8980]">
              {targetCount ? "Try another filter, or set a target for a different area." : "Start with a store-wide sales or expense target. Actuals will appear here as POS activity comes in."}
            </p>
            {!targetCount && <button onClick={() => showForm()} data-testid="button-add-first-budget-target" className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[#b9cec0] bg-white px-4 py-2.5 text-sm font-semibold text-[#315d4c] transition hover:bg-[#f4f8f3] active:scale-[.98]"><Plus className="h-4 w-4" /> Create first target</button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr className="bg-[#f8faf7] text-[10px] font-bold uppercase tracking-[.13em] text-[#819087]">
                  <th className="px-5 py-3">Target / scope</th>
                  <th className="px-4 py-3 text-right">Target</th>
                  <th className="px-4 py-3 text-right">Actual</th>
                  <th className="px-4 py-3 text-right">Variance</th>
                  <th className="px-4 py-3">Progress</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0eb]">
                {targets.map((target) => (
                  <BudgetRow key={target.id} target={target} onEdit={() => showForm(target)} onDelete={() => remove(target)} deleting={deleteTarget.isPending && deleteTarget.variables?.id === target.id} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center gap-2 border-t border-[#edf0eb] bg-[#fbfcfa] px-5 py-3 text-[11px] text-[#829087]">
          <Clock3 className="h-3.5 w-3.5" />
          Actuals update from recorded POS activity. Favorable variance is interpreted differently for sales and expenses.
        </div>
      </section>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#18332d]/35 p-0 backdrop-blur-[2px] sm:items-center sm:p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }}>
          <section role="dialog" aria-modal="true" aria-labelledby="budget-form-title" data-testid="dialog-budget-target" className="w-full max-w-lg rounded-t-2xl border border-[#d8e3d9] bg-[#fffefa] shadow-[0_22px_80px_rgba(23,53,43,.2)] sm:rounded-2xl">
            <div className="flex items-start justify-between border-b border-[#e8eee8] px-5 py-5 sm:px-6">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[.16em] text-[#789080]">{monthLabel(month)}</p>
                <h3 id="budget-form-title" className="mt-1 font-['Manrope'] text-xl font-extrabold tracking-tight text-[#24443a]">{editing ? "Adjust target" : "Set a budget target"}</h3>
                <p className="mt-1 text-xs text-[#7d8b81]">One clear number for one part of your business.</p>
              </div>
              <button onClick={closeForm} data-testid="button-close-budget-form" aria-label="Close budget target form" className="rounded-lg p-2 text-[#71827a] transition hover:bg-[#f0f4ef] hover:text-[#29483d]"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={save} className="space-y-5 px-5 py-5 sm:px-6">
              <div>
                <label className="mb-2 block text-xs font-semibold text-[#52675d]" htmlFor="budget-metric">Metric</label>
                <div className="grid grid-cols-2 gap-2">
                  {(["sales", "expenses"] as const).map((metric) => (
                    <button type="button" key={metric} disabled={!!editing} onClick={() => updateMetric(metric)} data-testid={`button-budget-metric-${metric}`} className={`rounded-lg border px-3 py-2.5 text-sm font-semibold capitalize transition ${form.metric === metric ? "border-[#7da38e] bg-[#eef5ed] text-[#2e624c]" : "border-[#e0e7e0] text-[#718078] hover:bg-[#f7f9f6]"} disabled:cursor-not-allowed disabled:opacity-70`}>{metric}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-[#52675d]" htmlFor="budget-scope-type">Apply target to</label>
                <div className="relative">
                  <select id="budget-scope-type" value={form.scopeType} onChange={(event) => updateScope(event.target.value as Scope)} disabled={!!editing} data-testid="select-budget-scope-type" className="h-11 w-full appearance-none rounded-lg border border-[#d5e0d6] bg-white px-3 pr-9 text-sm text-[#365248] outline-none transition focus:border-[#6d9782] focus:ring-2 focus:ring-[#b7d0bf]/50 disabled:bg-[#f5f7f4]">
                    <option value="store">Entire store</option>
                    <option value="branch">Branch</option>
                    <option value="category">{form.metric === "sales" ? "Product category" : "Expense category"}</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-3.5 h-4 w-4 text-[#829087]" />
                </div>
              </div>
              {form.scopeType === "branch" && (
                <div>
                  <label className="mb-2 block text-xs font-semibold text-[#52675d]" htmlFor="budget-branch">Branch</label>
                  <div className="relative">
                    <select id="budget-branch" value={form.scopeId} onChange={(event) => setForm((current) => ({ ...current, scopeId: event.target.value }))} disabled={!!editing} data-testid="select-budget-branch" className="h-11 w-full appearance-none rounded-lg border border-[#d5e0d6] bg-white px-3 pr-9 text-sm text-[#365248] outline-none focus:border-[#6d9782] focus:ring-2 focus:ring-[#b7d0bf]/50 disabled:bg-[#f5f7f4]">
                      <option value="">Choose a branch</option>
                      {(overview.branches ?? []).map((branch) => <option key={branch.id} value={String(branch.id)}>{branch.name}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-3.5 h-4 w-4 text-[#829087]" />
                  </div>
                </div>
              )}
              {isCategory && form.metric === "sales" && (
                <div>
                  <label className="mb-2 block text-xs font-semibold text-[#52675d]" htmlFor="budget-sales-category">Product category</label>
                  <SearchableCategoryPicker
                    id="budget-sales-category"
                    value={form.scopeId}
                    options={selectedScopeOptions.map((category) => ({ value: String(category.id), label: category.name }))}
                    placeholder="Choose a product category"
                    testId="select-budget-sales-category"
                    onChange={(value) => setForm((current) => ({ ...current, scopeId: value }))}
                    disabled={!!editing}
                  />
                </div>
              )}
              {isCategory && form.metric === "expenses" && (
                <div>
                  <label className="mb-2 block text-xs font-semibold text-[#52675d]" htmlFor="budget-expense-category">Expense category</label>
                  {!editing ? (
                    <>
                      <SearchableCategoryPicker
                        id="budget-expense-category"
                        value={form.scopeId === "custom" ? "custom" : form.scopeId}
                        options={[
                          ...(overview.expenseCategories ?? []).map((category) => ({ value: category, label: category })),
                          { value: "custom", label: "Add a new category…" },
                        ]}
                        placeholder="Choose a category"
                        testId="select-budget-expense-category"
                        onChange={(value) => setForm((current) => ({ ...current, scopeId: value, customCategory: "" }))}
                      />
                      {isCustomExpenseCategory && <div className="mt-3">
                        <input id="budget-custom-category" value={form.customCategory} maxLength={80} onChange={(event) => setForm((current) => ({ ...current, customCategory: event.target.value }))} placeholder="Name this expense category" data-testid="input-budget-custom-category" className="h-11 w-full rounded-lg border border-[#d5e0d6] bg-white px-3 text-sm text-[#365248] outline-none placeholder:text-[#a1aaa3] focus:border-[#6d9782] focus:ring-2 focus:ring-[#b7d0bf]/50" />
                        <div className="mt-1.5 flex justify-between text-[10px] text-[#89958c]"><span>Up to 80 characters</span><span>{form.customCategory.length}/80</span></div>
                      </div>}
                    </>
                  ) : (
                    <input value={form.customCategory || editing.scopeName} readOnly data-testid="input-budget-edit-category" className="h-11 w-full rounded-lg border border-[#d5e0d6] bg-[#f5f7f4] px-3 text-sm text-[#567064]" />
                  )}
                </div>
              )}
              {form.scopeType === "store" && <p className="rounded-lg border border-[#e2ebe2] bg-[#f6f9f5] px-3 py-2.5 text-xs leading-5 text-[#74857a]">This target compares the whole store against its monthly POS total.</p>}
              <div>
                <label className="mb-2 block text-xs font-semibold text-[#52675d]" htmlFor="budget-target-amount">Monthly target</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-sm font-semibold text-[#789080]">₦</span>
                  <input id="budget-target-amount" type="number" min="0.01" step="0.01" value={form.targetAmount} onChange={(event) => setForm((current) => ({ ...current, targetAmount: event.target.value }))} placeholder="0.00" data-testid="input-budget-target-amount" className="h-11 w-full rounded-lg border border-[#d5e0d6] bg-white pl-8 pr-3 font-['IBM_Plex_Mono'] text-sm tabular-nums text-[#365248] outline-none placeholder:text-[#a1aaa3] focus:border-[#6d9782] focus:ring-2 focus:ring-[#b7d0bf]/50" />
                </div>
              </div>
              {formError && <p role="alert" data-testid="error-budget-form" className="rounded-lg border border-[#ecd8d1] bg-[#fff7f3] px-3 py-2 text-xs text-[#a14e38]">{formError}</p>}
              <div className="flex flex-col-reverse gap-2 border-t border-[#edf0eb] pt-4 sm:flex-row sm:justify-end">
                <button type="button" onClick={closeForm} data-testid="button-cancel-budget-form" className="h-10 rounded-lg border border-[#d8e2d8] px-4 text-sm font-semibold text-[#66786e] transition hover:bg-[#f5f8f4]">Cancel</button>
                <button type="submit" disabled={saveTarget.isPending} data-testid="button-save-budget-target" className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#174f45] px-5 text-sm font-semibold text-white transition hover:bg-[#103d35] active:scale-[.98] disabled:cursor-wait disabled:opacity-65">
                  {saveTarget.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {saveTarget.isPending ? "Saving…" : editing ? "Save changes" : "Save target"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

function BudgetRow({ target, onEdit, onDelete, deleting }: { target: BudgetVariance; onEdit: () => void; onDelete: () => void; deleting: boolean }) {
  const progress = target.targetAmount > 0 ? Math.max(0, Math.min(100, target.actualAmount / target.targetAmount * 100)) : 0;
  const varianceLabel = `${target.varianceAmount > 0 ? "+" : target.varianceAmount < 0 ? "−" : ""}${money(Math.abs(target.varianceAmount))}`;
  const variancePercent = Number.isFinite(target.variancePercent) ? Math.abs(target.variancePercent) : 0;
  const varianceTone = target.favorable ? "text-[#34744d]" : "text-[#a85d47]";
  return (
    <tr data-testid={`row-budget-target-${target.id}`} className="group transition-colors hover:bg-[#fbfcf9]">
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <span className={`h-9 w-1 rounded-full ${target.metric === "sales" ? "bg-[#7ca98a]" : "bg-[#d2a084]"}`} />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#304b40]" data-testid={`text-budget-scope-${target.id}`}>{target.scopeName}</span>
              <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[.1em] ${target.metric === "sales" ? "bg-[#edf5ed] text-[#517d5d]" : "bg-[#f7eee8] text-[#9a705a]"}`} data-testid={`badge-budget-metric-${target.id}`}>{target.metric}</span>
            </div>
            <span className="mt-1 block text-[11px] capitalize text-[#8a968d]" data-testid={`text-budget-scope-type-${target.id}`}>{target.scopeType === "category" ? (target.metric === "sales" ? "Product category" : "Expense category") : target.scopeType}</span>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-right font-['IBM_Plex_Mono'] text-xs tabular-nums text-[#526a5f]" data-testid={`value-budget-target-${target.id}`}>{money(target.targetAmount)}</td>
      <td className="px-4 py-4 text-right font-['IBM_Plex_Mono'] text-xs tabular-nums text-[#304b40]" data-testid={`value-budget-actual-${target.id}`}>{money(target.actualAmount)}</td>
      <td className="px-4 py-4 text-right">
        <span className={`block font-['IBM_Plex_Mono'] text-xs font-medium tabular-nums ${varianceTone}`} data-testid={`value-budget-variance-${target.id}`}>{varianceLabel}</span>
        <span className={`mt-1 inline-flex items-center gap-0.5 text-[10px] ${varianceTone}`} data-testid={`value-budget-variance-percent-${target.id}`}>{target.varianceAmount > 0 ? <ArrowUpRight className="h-3 w-3" /> : target.varianceAmount < 0 ? <ArrowDownRight className="h-3 w-3" /> : null}{variancePercent.toFixed(1)}%</span>
      </td>
      <td className="px-4 py-4">
        <div className="mb-1.5 flex items-center justify-between gap-2 text-[10px]">
          <span className={`font-semibold ${target.favorable ? "text-[#4b7857]" : "text-[#a2745e]"}`} data-testid={`status-budget-favorable-${target.id}`}>{target.favorable ? "On track" : "Needs attention"}</span>
          <span className="font-['IBM_Plex_Mono'] tabular-nums text-[#78887d]" data-testid={`text-budget-progress-${target.id}`}>{progress.toFixed(0)}%</span>
        </div>
        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-[#e9eee8] sm:w-32" role="progressbar" aria-label={`${target.scopeName} target progress`} aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} data-testid={`progress-budget-target-${target.id}`}>
          <div className={`h-full rounded-full transition-all duration-300 ${target.favorable ? "bg-[#79a083]" : "bg-[#c08a6d]"}`} style={{ width: `${progress}%` }} />
        </div>
      </td>
      <td className="px-5 py-4 text-right">
        <div className="inline-flex items-center gap-1">
          <button onClick={onEdit} data-testid={`button-edit-budget-${target.id}`} aria-label={`Edit ${target.scopeName} target`} className="rounded-md p-2 text-[#7b8d82] transition hover:bg-[#edf4ed] hover:text-[#37624e] active:scale-95"><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={onDelete} disabled={deleting} data-testid={`button-delete-budget-${target.id}`} aria-label={`Delete ${target.scopeName} target`} className="rounded-md p-2 text-[#a18a7e] transition hover:bg-[#fbefeb] hover:text-[#a55039] active:scale-95 disabled:opacity-50">{deleting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}</button>
        </div>
      </td>
    </tr>
  );
}

export default function AccountingBudgets() {
  return (
    <div className="mx-auto max-w-[1240px] space-y-6 px-1 pb-10 sm:px-2" data-testid="page-accounting-budgets">
      <div>
        <h1 className="font-['Manrope'] text-2xl font-extrabold tracking-tight text-[#243b36] sm:text-[1.8rem]" data-testid="heading-accounting-budgets">Monthly budgets</h1>
        <p className="mt-1 text-sm text-[#718178]">Plan monthly sales and expenses against the numbers from your register.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise"]} feature="Monthly Budgets">
        <BudgetContent />
      </PlanGate>
    </div>
  );
}
